import { ResolvedTheme, ThemePreference } from 'types';

export const THEME_PREFERENCE_STORAGE_KEY = 'themePreference';
export const LEGACY_THEME_STORAGE_KEY = 'theme';
export const SYSTEM_DARK_MODE_QUERY = '(prefers-color-scheme: dark)';

export const isThemePreference = (
  value: string | null
): value is ThemePreference =>
  value === 'system' || value === 'light' || value === 'dark';

const getLocalStorage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

export const readThemePreference = (): ThemePreference => {
  const storage = getLocalStorage();
  if (!storage) {
    return 'system';
  }

  try {
    const storedPreference = storage.getItem(THEME_PREFERENCE_STORAGE_KEY);
    storage.removeItem(LEGACY_THEME_STORAGE_KEY);

    if (isThemePreference(storedPreference)) {
      return storedPreference;
    }

    if (storedPreference !== null) {
      storage.removeItem(THEME_PREFERENCE_STORAGE_KEY);
    }
  } catch {
    return 'system';
  }

  return 'system';
};

export const persistThemePreference = (preference: ThemePreference): void => {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(THEME_PREFERENCE_STORAGE_KEY, preference);
    storage.removeItem(LEGACY_THEME_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
};

export const getSystemThemeMediaQuery = (): MediaQueryList | null => {
  try {
    return typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function'
      ? window.matchMedia(SYSTEM_DARK_MODE_QUERY)
      : null;
  } catch {
    return null;
  }
};

export const resolveThemePreference = (
  preference: ThemePreference,
  mediaQuery: MediaQueryList | null = getSystemThemeMediaQuery()
): ResolvedTheme => {
  if (preference !== 'system') {
    return preference;
  }

  return mediaQuery?.matches ? 'dark' : 'light';
};
